export const MAX_FILE_SIZE = 2 * 1024 * 1024
export const MAX_TOTAL_SIZE = 5 * 1024 * 1024
export const MAX_FILE_COUNT = 5

export const ACCEPTED_MIME_TYPES = [
  'text/markdown',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf'
]

const EXTENSION_TO_MIME = {
  md: 'text/markdown',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  pdf: 'application/pdf'
}

export const ACCEPTED_EXTENSIONS = Object.keys(EXTENSION_TO_MIME)

export const ACCEPTED_EXTENSIONS_LABEL = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(', ')

export const FILE_INPUT_ACCEPT = [
  ...ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`),
  ...ACCEPTED_MIME_TYPES
].join(',')

const acceptsPdf = (aiVersion) => !(aiVersion >= 20)

export const acceptedMimeTypesFor = (aiVersion) => ACCEPTED_MIME_TYPES
  .filter((mimeType) => mimeType !== 'application/pdf' || acceptsPdf(aiVersion))

export const acceptedExtensionsLabelFor = (aiVersion) => ACCEPTED_EXTENSIONS
  .filter((ext) => ext !== 'pdf' || acceptsPdf(aiVersion))
  .map((ext) => `.${ext}`)
  .join(', ')

export const fileInputAcceptFor = (aiVersion) => {
  const mimeTypes = acceptedMimeTypesFor(aiVersion)

  return [
    ...ACCEPTED_EXTENSIONS.filter((ext) => mimeTypes.includes(EXTENSION_TO_MIME[ext])).map((ext) => `.${ext}`),
    ...mimeTypes
  ].join(',')
}

const MIME_TO_EXTENSION = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'text/markdown': 'md',
  'text/plain': 'txt'
}

const GENERIC_PASTE_NAME = /^image\.(png|jpe?g|webp)$/i

export const resolveMimeType = (file) => {
  const ext = file.name.toLowerCase().split('.').pop()
  if (EXTENSION_TO_MIME[ext]) {
    return EXTENSION_TO_MIME[ext]
  }
  if (file.type) {
    return file.type
  }

  return 'application/octet-stream'
}

const pad2 = (n) => String(n).padStart(2, '0')

const timestampForFilename = () => {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${pad2(d.getHours())}-${pad2(d.getMinutes())}-${pad2(d.getSeconds())}`
}

export const dedupeName = (proposedName, existingNames) => {
  if (!existingNames.includes(proposedName)) {
    return proposedName
  }
  const dotIndex = proposedName.lastIndexOf('.')
  const base = dotIndex >= 0 ? proposedName.slice(0, dotIndex) : proposedName
  const ext = dotIndex >= 0 ? proposedName.slice(dotIndex) : ''
  let n = 2
  while (existingNames.includes(`${base} (${n})${ext}`)) {
    n++
  }

  return `${base} (${n})${ext}`
}

const typePrefix = (mimeType) => {
  if (mimeType?.startsWith('image/')) {
    return 'image'
  }
  if (mimeType?.startsWith('text/')) {
    return 'text'
  }
  if (mimeType === 'application/pdf') {
    return 'pdf'
  }

  return 'file'
}

export const renameGenericFile = (file, existingNames) => {
  const isGeneric = !file.name || GENERIC_PASTE_NAME.test(file.name)
  if (!isGeneric) {
    return file
  }
  const extension = MIME_TO_EXTENSION[file.type] || 'bin'
  const prefix = typePrefix(file.type)
  const proposed = `${prefix}-${timestampForFilename()}.${extension}`
  const finalName = dedupeName(proposed, existingNames)

  return new File([file], finalName, { type: file.type })
}
